import { SITE_NAME, siteNameStart, splitSeparatorSiteSuffix } from '~/composables/siteName'

// CMS titles arrive clean, with a baked "| 180 Medical" suffix, or as prose ending in the site name.
// Don't drop a trailing site name from prose: it orphans a preposition before the appended suffix.
export function composeTitle(raw?: string): string {
  const title = raw?.trim()
  if (!title) {
    return SITE_NAME
  }
  if (siteNameStart(title) === -1) {
    return `${title} - ${SITE_NAME}`
  }
  const head = splitSeparatorSiteSuffix(title)
  return head ? `${head} - ${SITE_NAME}` : title
}

// Left unset when neither flag is on, so the site-level directive nuxt-robots emits
// (which is what keeps lower environments noindex) stays the one that applies.
function robotsDirective(noIndex?: boolean, noFollow?: boolean): string | undefined {
  if (!noIndex && !noFollow) {
    return undefined
  }
  return `${noIndex ? 'noindex' : 'index'}, ${noFollow ? 'nofollow' : 'follow'}`
}

export function useSeo(metaInfo?: {
  title?: string
  documentTitle?: string
  description?: string
  url?: string
  images?: { url: string, altText?: string }[]
  skus?: string
  noIndex?: boolean
  noFollow?: boolean
}) {
  const title = metaInfo?.documentTitle || composeTitle(metaInfo?.title)
  const primaryImage = metaInfo?.images?.[0]

  // We compose the full <title> ourselves (including the site name), so suppress
  // Nuxt's global titleTemplate to avoid a second " - 180 Medical" being appended.
  useHead({
    title,
    titleTemplate: null,
    meta: [
      ...(metaInfo?.images?.flatMap(img => [
        { property: 'og:image', content: img.url },
        { property: 'og:image:alt', content: img.altText || '' },
        { property: 'og:image:width', content: 1200 },
        { property: 'og:image:height', content: 630 },
      ]) || []),

      // Product meta (only if SKUs exist)
      ...(metaInfo?.skus?.length
        ? [
            { property: 'product:price:currency', content: 'USD' },
            { property: 'product:availability', content: 'instock' },
            { property: 'og:availability', content: 'instock' },
            { property: 'product:retailer_item_id', content: metaInfo.skus },
            { property: 'product:condition', content: 'new' },
          ]
        : []),

    ],
  })

  useSeoMeta({
    description: metaInfo?.description || '',
    ogTitle: () => title,
    ogDescription: () => metaInfo?.description,
    ogImage: () => primaryImage?.url,
    twitterCard: 'summary_large_image',
    twitterImage: () => primaryImage?.url,
    robots: robotsDirective(metaInfo?.noIndex, metaInfo?.noFollow),
  })
}
